<template>
	<div>
		<!-- Loading Indicator -->
		<div v-if="loading" class="text-center my-3">
			<div class="d-flex flex-column align-items-center">
				<div class="spinner-border text-primary mb-3" role="status"></div>
				<span class="text-muted">{{ __('Loading case details...') }}</span>
			</div>
		</div>

		<!-- Case Details -->
		<div v-if="!loading && caseDetails">
			<div :class="['card-body', priorityStyle(caseDetails.priority)]">
				<h3>{{ caseDetails.title }}</h3>
				<div class="row">
					<div class="col-md-6">
						<p class="mb-2 capitalize">
							<strong>{{ __('Type') }}:</strong> {{ __(caseDetails.type) }}
						</p>
						<p class="mb-2 capitalize">
							<strong>{{ __('Status') }}:</strong> {{ __(caseDetails.status) }}
						</p>
						<p class="mb-2">
							<strong>{{ __('Pinned') }}:</strong>
							{{ caseDetails.is_pinned ? __('Yes') : __('No') }}
						</p>
					</div>
					<div class="col-md-6">
						<p class="mb-2">
							<strong>{{ __('Created By') }}:</strong>
							{{ caseDetails.created_by.name }}
						</p>
						<p class="mb-2">
							<strong>{{ __('Created At') }}:</strong>
							{{ formatDate(caseDetails.created_at) }}
						</p>
						<p class="mb-2">
							<strong>{{ __('Last Update') }}:</strong>
							{{ formatDate(caseDetails.updated_at) }}
						</p>
					</div>
				</div>

				<!-- Description -->
				<div class="mt-4 description-section">
					<h5 class="description-title">{{ __('Description') }}:</h5>
					<p class="description-content">
						{{ caseDetails.description || __('No description available.') }}
					</p>
				</div>

				<!-- Review Section -->
				<ReviewCard v-if="caseDetails.review" :review="caseDetails.review" class="mt-2" />
				<template v-if="caseDetails.surveyResponse">
					<button class="btn btn-link text-primary font-weight-bold w-100 text-left border my-2" type="button"
						data-toggle="collapse" data-target="#surveyCardCollapse" aria-controls="surveyCardCollapse">
						{{ __('Show Survey Response') }}
					</button>

					<!-- Collapsible Review Card -->
					<div class="collapse" id="surveyCardCollapse">
						<SurveysSingleResponse :response="caseDetails.surveyResponse" :action-buttons="false" />
					</div>

				</template>

				<!-- Assignees -->
				<div class="mt-4">
					<h5 class="description-title">{{ __('Assignees') }}:</h5>
					<div class="d-flex flex-row flex-wrap">
						<span v-for="assignee in caseDetails.assignees" class="d-flex align-items-center avatar-pill">
							<img :src="assignee?.photo_url || '/assets/img/1.0/avatar.png'" alt="User Avatar"
								class="assignee-avatar img-thumbnail" width="30" height="30" />
							<strong>{{ assignee.entity?.name || assignee.entity?.email }}</strong>
						</span>
					</div>
				</div>

				<!-- Departments Section -->
				<div class="mt-4" v-if="caseDetails.departments && caseDetails.departments.length > 0">
					<h5 class="description-title">{{ __('Departments') }}:</h5>
					<div class="d-flex flex-row flex-wrap">
						<span v-for="department in caseDetails.departments" :key="department.key"
							class="d-flex align-items-center department-pill">
							<!-- Use a building icon -->
							<i class="department-icon"></i>
							<strong>{{ labelizeGeneral(department.label) }}</strong>
						</span>
					</div>
				</div>
			</div>

			<!-- Comments Section -->
			<div class="card-body" style="background-color: #f8f8f8">
				<div v-if="caseDetails.comments && caseDetails.comments.length > 0">
					<div v-for="comment in caseDetails.comments" :key="comment.id"
						class="d-flex align-items-start mb-3">
						<img :src="comment?.entity?.photo_url || '/assets/img/1.0/avatar.png'" alt="User Avatar"
							class="comment-avatar img-thumbnail" />
						<div class="comment-content">
							<p class="comment-header">
								<strong>{{ comment.entity?.name || comment?.email }}</strong>
								<span class="comment-date">{{
									formatDate(comment.created_at)
									}}</span>
							</p>
							<p class="comment-text">{{ comment.content }}</p>
						</div>
					</div>
				</div>

				<!-- Add Comment -->
				<div>
					<div class="form-group d-flex">
						<img :src="currentUserAvatar" alt="User Avatar" class="comment-avatar img-thumbnail" />
						<textarea v-model="commentReply" class="comment-content"
							:placeholder="__('Write your reply here...')">
						</textarea>
					</div>

					<!-- Status Message -->
					<div v-if="statusMessage.message" :class="`alert alert-${statusMessage.type} mt-3`" role="alert">
						{{ statusMessage.message }}
					</div>

					<button class="btn btn-primary mt-2" :disabled="isSubmitting" @click="submitComment">
						{{ isSubmitting ? __('Posting...') : __('Post Comment') }}
					</button>
				</div>
			</div>
		</div>

		<!-- Case Not Found -->
		<div v-if="!loading && !caseDetails" class="text-center my-3">
			<p class="text-muted">{{ __('Case not found.') }}</p>
		</div>
	</div>
</template>

<script>
import CasesService from '@/services/cases-service';
import ReviewCard from '@/components/cases/ReviewCard.vue';
import SurveysService from '@/services/surveys-service';
import SurveysSingleResponse from '../surveys/SurveysSingleResponse.vue';

export default {

	components: {
		ReviewCard,
		SurveysSingleResponse,
	},

	data() {
		return {
			caseDetails: null,
			loading: false,
			commentReply: '',
			isSubmitting: false,
			statusMessage: { message: '', type: 'success' },
		};
	},

	computed: {
		currentUserAvatar() {
			return Spark?.state?.user?.photo_url || '/assets/img/1.0/avatar.png';
		},
	},

	mounted() {
		this.$eventHub.$on('activateTab', this.handleTabActivation);
	},

	beforeDestroy() {
		this.$eventHub.$off('activateTab', this.handleTabActivation);
	},

	methods: {

		async handleTabActivation(id, caseId) {
			if (id === 'case-details') {
				this.getCase(caseId);
			}
		},

		async getCase(caseId, silent = false) {
			this.loading = true && !silent;
			try {
				const response = await CasesService.getCaseDetails(
					Spark.state.currentTeam.id,
					caseId,
				);
				this.caseDetails = response;
				if (this.caseDetails.survey_response_id) {
					const surveyResponse = await SurveysService.responses.index(1, {
						response_id: this.caseDetails.survey_response_id,
					});
					this.caseDetails.surveyResponse = surveyResponse?.data?.[0];
				}
			} catch (error) {
				console.error('Error fetching case details:', error);
				this.caseDetails = null;
			} finally {
				this.loading = false;
			}
		},

		async submitComment() {
			if (!this.commentReply.trim()) return;

			this.isSubmitting = true;
			const payload = {
				comment: this.commentReply,
				user: {
					id: Spark.state.user.id,
					email: Spark.state.user.email,
				},
			};

			try {
				await CasesService.postComment(
					Spark.state.currentTeam.id,
					this.caseDetails.id,
					payload,
				);
				this.commentReply = '';
				await this.getCase(this.caseDetails.id, true);
			} catch (error) {
				console.error('Error posting comment:', error);
				this.setStatusMessage(Object.values(error.errors).join(' '), 'danger');
			} finally {
				this.isSubmitting = false;
			}
		},

		priorityStyle(priority) {
			const priorities = {
				0: 'low-priority',
				1: 'medium-priority',
				2: 'high-priority',
			};
			return priorities[priority] || '';
		},

		formatDate(date, includeTime = true) {
			if (!date) return null;

			const parsedDate = new Date(date.replace(' ', 'T'));
			const options = { day: '2-digit', month: 'short', year: 'numeric' };

			if (includeTime) {
				options.hour = '2-digit';
				options.minute = '2-digit';
				options.hour12 = false;
			}

			return new Intl.DateTimeFormat('en-GB', options).format(parsedDate);
		},

		setStatusMessage(message, type = 'success') {
			this.statusMessage = { message, type };
			setTimeout(() => {
				this.statusMessage = { message: '', type: 'success' };
			}, 5000);
		},
	},

};
</script>

<style scoped>
.capitalize {
	text-transform: capitalize;
}

.high-priority {
	border: 1px solid #e9eaec;
	border-left: 4px solid #ca3438;
}

.medium-priority {
	border: 1px solid #e9eaec;
	border-left: 4px solid #ffcc29;
}

.low-priority {
	border: 1px solid #e9eaec;
	border-left: 4px solid #01e001;
}

.description-section {
	background: #f5f5f5;
	padding: 15px;
	border-radius: 4px;
}

.description-title {
	font-size: 18px;
	font-weight: bold;
	color: #333;
}

.description-content {
	font-size: 16px;
	color: #555;
	margin-top: 5px;
	line-height: 1.6;
}

.comment-card {
	display: flex;
	padding: 15px;
	border-radius: 4px;
	margin-bottom: 15px;
}

.comment-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	margin-right: 15px;
}

.comment-content {
	flex: 1;
	border: 1px solid #ddd;
	border-radius: 8px;
	background: #e0e0e0;
	padding: 10px;
}

.comment-header {
	font-size: 14px;
	margin: 0 0 5px;
}

.comment-header strong {
	margin-right: 10px;
}

.comment-date {
	font-size: 12px;
	color: #999;
}

.comment-text {
	font-size: 14px;
	color: #333;
}

.review-name {
	color: #00aef1;
	font-weight: 700;
	font-size: 14px;
}

.avatar-pill {
	display: inline-block;
	background-color: #ffffff;
	/* White background */
	border-radius: 25px;
	/* Rounded pill */
	padding: 5px 15px;
	/* Padding around content */
	margin: 5px;
	/* Margin for spacing between pills */
	font-size: 14px;
	/* Slightly larger font */
	font-weight: 600;
	/* Bold font for names */
	color: #333;
	/* Text color */
	border: 1px solid #ddd;
	/* Light border */
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
	/* Drop shadow for the pill */
	align-items: center;
	/* Center the content vertically */
}

.assignee-avatar {
	border-radius: 50%;
	/* Rounded avatar */
	background-color: #e0e0e0;
	/* Default background color if no image */
	border: 2px solid #ffffff;
	/* White border around avatar */
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
	/* Drop shadow for the avatar */
}

.department-pill {
	display: inline-block;
	background-color: #ffffff; /* White background */
	border-radius: 25px; /* Rounded pill */
	padding: 5px 15px; /* Padding around content */
	margin: 5px; /* Margin for spacing between pills */
	font-size: 14px; /* Slightly larger font */
	font-weight: 600; /* Bold font for names */
	color: #333; /* Text color */
	border: 1px solid #ddd; /* Light border */
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Drop shadow for the pill */
	align-items: center; /* Center the content vertically */
}

.department-icon {
	display: inline-block;
	width: 20px;
	height: 20px;
	background: url('/assets/img/1.0/hotel.png') no-repeat center center;
	background-size: contain;
	margin-right: 10px; /* Space between icon and label */
	color: #00aef1; /* Icon color */
	font-size: 18px; /* Slightly larger icon size */
}

</style>
